import React, { useMemo } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { Flex, Box } from 'rebass/styled-components';

import { Profile } from './steps/profile';
import { useOnboarding } from './hooks/useOnboarding';
import { Stepper } from './components/stepper';
import { NetworkKind } from './steps/networkKinds';
import { Goals } from './steps/goals';
import { LinkedIn } from './steps/linkedin';
import { ShareContacts } from './steps/shareContacts';
import { Team } from './steps/team';

const Walkthrough = () => {
  const {
    isLoadingUpdateUser,
    updateUserProfile,
    handleNextStep,
    goals,
    users,
    setUsers,
    setGoals,
    activeNetwork,
    updateUserGoals,
    isLoadingUpdateUserGoals,
    isLoadingUpdateNetworkKind,
    isLoadingAddAdmin,
    networkKind,
    updateNetworkKind,
    updateGoals,
    user,
    updateTeam,
    showNetworkKindStep,
    profileMode,
    isExtensionInstalled,
    params,
  } = useOnboarding();

  const steps = useMemo(() => {
    const result = [];

    // Profile step - show if onboarding not completed or if accessing via employer slug
    if (!user?.onboardingCompleted || params?.employerSlug) {
      result.push('profile');
    }

    // Network kind step - show if flag is set or onboarding not completed
    // Skip network-kind step if employerSlug is present
    if (!params?.employerSlug && (showNetworkKindStep || !user?.onboardingCompleted)) {
      result.push('network-kind');
    }

    // Goals step - always included
    result.push('goals');

    // Contact sync step - use share-contacts if extension installed, otherwise linkedin-sync
    result.push(isExtensionInstalled ? 'share-contacts' : 'linkedin-sync');

    // Team step - always included
    result.push('team');

    return result;
  }, [user?.onboardingCompleted, params?.employerSlug, showNetworkKindStep, isExtensionInstalled]);

  return (
    <Box>
      {activeNetwork && <Stepper steps={steps} slug={activeNetwork.slug} />}
      <Flex
        sx={{
          gap: '56px',
          flexDirection: 'column',
          px: '16px',
          mx: 'auto',
          height: '100%',
        }}
      >
        <Switch>
          <Route
            exact
            path={[
              '/networks/:networkSlug/welcome/onboarding/profile',
              '/networks/:networkSlug/company/:employerSlug/welcome/onboarding/profile',
            ]}
          >
            <Redirect
              to={
                params.employerSlug
                  ? `/networks/${activeNetwork.slug}/company/${params.employerSlug}/welcome/onboarding/profile/${profileMode}`
                  : `/networks/${activeNetwork.slug}/welcome/onboarding/profile/${profileMode}`
              }
            />
          </Route>
          <Route
            exact
            path={[
              '/networks/:networkSlug/welcome/onboarding/profile/:mode',
              '/networks/:networkSlug/company/:employerSlug/welcome/onboarding/profile/:mode',
            ]}
          >
            <Profile
              isLoadingUpdateUser={isLoadingUpdateUser}
              updateUserProfile={updateUserProfile}
              network={activeNetwork}
              user={user}
            />
          </Route>
          <Route exact path="/networks/:networkSlug/welcome/onboarding/network-kind">
            {showNetworkKindStep ? (
              <NetworkKind
                handleNextStep={handleNextStep}
                networkKind={networkKind}
                network={activeNetwork}
                isLoadingUpdateNetworkKind={isLoadingUpdateNetworkKind}
                updateNetworkKind={updateNetworkKind}
              />
            ) : (
              <Redirect to={`/networks/${activeNetwork.slug}/welcome/onboarding/goals`} />
            )}
          </Route>
          <Route exact path="/networks/:networkSlug/welcome/onboarding/goals">
            <Goals
              handleNextStep={handleNextStep}
              goals={goals}
              updateUserGoals={updateUserGoals}
              isLoadingUpdateUserGoals={isLoadingUpdateUserGoals}
              setGoals={setGoals}
              network={activeNetwork}
              updateGoals={updateGoals}
              kind={networkKind}
            />
          </Route>
          <Route exact path="/networks/:networkSlug/welcome/onboarding/linkedin-sync">
            <LinkedIn
              handleNextStep={handleNextStep}
              activeNetwork={activeNetwork}
              isExtensionInstalled={isExtensionInstalled}
            />
          </Route>
          <Route exact path="/networks/:networkSlug/welcome/onboarding/share-contacts">
            <ShareContacts handleNextStep={handleNextStep} activeNetwork={activeNetwork} />
          </Route>
          <Route exact path="/networks/:networkSlug/welcome/onboarding/team">
            <Team users={users} setUsers={setUsers} isLoadingAddAdmin={isLoadingAddAdmin} updateTeam={updateTeam} />
          </Route>
        </Switch>
      </Flex>
    </Box>
  );
};

export default Walkthrough;
